import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Instagram, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { galleryImages } from '../data/mock';
import { useSettings } from '../context/SettingsContext';

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const { businessInfo } = useSettings();

  const categories = ['all', 'nail art', 'gel manicure', 'french manicure', 'special occasion'];
  
  const filteredImages = activeFilter === 'all' 
    ? galleryImages 
    : galleryImages.filter(img => img.category === activeFilter);

  return (
    <main>
      {/* Hero */}
      <section className="section-padding-small bg-[var(--bg-secondary)]">
        <div className="container">
          <div className="max-w-2xl">
            <p className="text-sm uppercase tracking-widest text-[var(--text-secondary)] mb-2">Portfolio</p>
            <h1 className="hero-medium mb-4">Gallery</h1>
            <p className="body-large text-[var(--text-secondary)]">
              Browse through my collection of nail designs. Each set is uniquely created 
              with attention to detail and personal style.
            </p>
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="section-padding">
        <div className="container">
          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-12">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveFilter(cat)}
                className={`px-5 py-2 text-sm capitalize transition-colors border ${
                  activeFilter === cat
                    ? 'bg-[var(--text-primary)] text-[var(--bg-primary)] border-[var(--text-primary)]'
                    : 'bg-transparent text-[var(--text-secondary)] border-[var(--border-light)] hover:border-[var(--text-primary)]'
                }`}
                style={{ borderRadius: 0 }}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Image Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredImages.map((image) => (
              <button
                key={image.id}
                onClick={() => setSelectedImage(image)}
                className="aspect-square overflow-hidden group relative"
              >
                <img
                  src={image.url}
                  alt={image.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
              </button>
            ))}
          </div>

          {/* Empty State */}
          {filteredImages.length === 0 && (
            <div className="text-center py-16">
              <p className="body-regular text-[var(--text-secondary)]">
                No images found in this category.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Instagram CTA */}
      <section className="section-padding-small bg-[var(--bg-secondary)]">
        <div className="container">
          <div className="text-center max-w-2xl mx-auto">
            <Instagram size={32} className="mx-auto mb-4 text-[var(--text-primary)]" />
            <h2 className="heading-2 mb-4">Follow on Instagram</h2>
            <p className="body-regular text-[var(--text-secondary)] mb-6">
              See more of my work and stay updated with the latest nail trends and designs.
            </p>
            <a
              href={businessInfo.instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
            >
              {businessInfo.instagram}
              <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* Book CTA */}
      <section className="section-padding">
        <div className="container">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="heading-1 mb-4">Love What You See?</h2>
            <p className="body-regular text-[var(--text-secondary)] mb-8">
              Book your appointment and let's create your perfect nail design together.
            </p>
            <Link to="/book" className="btn-primary">
              Book Appointment
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Image Modal */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-3xl p-0 bg-[var(--bg-primary)] border-none">
          <DialogTitle className="sr-only">{selectedImage?.title || 'Gallery Image'}</DialogTitle>
          {selectedImage && (
            <div>
              <img
                src={selectedImage.url}
                alt={selectedImage.title}
                className="w-full max-h-[80vh] object-contain"
              />
              <div className="p-6">
                <h3 className="heading-3">{selectedImage.title}</h3>
                <p className="body-small text-[var(--text-secondary)] capitalize">{selectedImage.category}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
};

export default Gallery;
